export interface DocItem {
  slug: string;
  title: string;
  file: string;
}

export interface DocSection {
  title: string;
  items: DocItem[];
}

export type ProductId = 'auth' | 'live';

export interface DocProduct {
  id: ProductId;
  label: string;
  tagline: string;
  defaultSlug: string;
  sections: DocSection[];
}

export const DOC_PRODUCTS: DocProduct[] = [
  {
    id: 'auth',
    label: 'Pryma Auth',
    tagline: 'Drop-in authentication and authorization for your apps.',
    defaultSlug: 'overview',
    sections: [
      {
        title: 'Getting Started',
        items: [
          { slug: 'overview', title: 'Overview', file: 'overview.md' },
          { slug: 'quickstart', title: 'Quickstart', file: 'quickstart.md' },
          { slug: 'concepts', title: 'Key concepts', file: 'concepts.md' },
        ],
      },
      {
        title: 'Integrations',
        items: [
          { slug: 'node', title: 'Node.js', file: 'integrations/node.md' },
          { slug: 'react', title: 'React', file: 'integrations/react.md' },
          { slug: 'ruby', title: 'Ruby on Rails', file: 'integrations/ruby.md' },
          { slug: 'python', title: 'Python', file: 'integrations/python.md' },
          { slug: 'go', title: 'Go', file: 'integrations/go.md' },
          { slug: 'java', title: 'Java / Spring Boot', file: 'integrations/java.md' },
          { slug: 'elixir', title: 'Elixir / Phoenix', file: 'integrations/elixir.md' },
        ],
      },
    ],
  },
  {
    id: 'live',
    label: 'Pryma Live',
    tagline: 'Realtime presence, state, and collaborative documents.',
    defaultSlug: 'live-overview',
    sections: [
      {
        title: 'Getting Started',
        items: [{ slug: 'live-overview', title: 'Overview', file: 'live/overview.md' }],
      },
      {
        title: 'Primitives',
        items: [
          { slug: 'live-presence', title: 'Presence', file: 'live/presence.md' },
          { slug: 'live-state', title: 'State', file: 'live/state.md' },
          { slug: 'live-doc', title: 'Doc', file: 'live/doc.md' },
        ],
      },
    ],
  },
];

export const DEFAULT_PRODUCT_ID: ProductId = 'auth';

const rawFiles = import.meta.glob('./content/**/*.md', {
  query: '?raw',
  import: 'default',
  eager: true,
}) as Record<string, string>;

export function getDocContent(file: string): string | null {
  const key = `./content/${file}`;
  return rawFiles[key] ?? null;
}

export function getProduct(id: ProductId): DocProduct {
  const found = DOC_PRODUCTS.find(p => p.id === id);
  if (!found) throw new Error(`Unknown doc product: ${id}`);
  return found;
}

export function getDocsForProduct(productId: ProductId): DocItem[] {
  return getProduct(productId).sections.flatMap(s => s.items);
}

export function getAllDocs(): DocItem[] {
  return DOC_PRODUCTS.flatMap(p => p.sections.flatMap(s => s.items));
}

export function getDocBySlug(slug: string): DocItem | null {
  return getAllDocs().find(d => d.slug === slug) ?? null;
}

export function getProductForSlug(slug: string): DocProduct | null {
  for (const product of DOC_PRODUCTS) {
    if (product.sections.some(s => s.items.some(i => i.slug === slug))) {
      return product;
    }
  }
  return null;
}

export const DEFAULT_DOC_SLUG = getProduct(DEFAULT_PRODUCT_ID).defaultSlug;
